/**
 * licenseCode.js — Browser-side license code validator (Electron renderer).
 *
 * Format: JRNFI-XXXX-XXXX-XXXX (prefix + 12 body chars, split as 4-identifier + 8-MAC)
 *
 * Alphabet: Crockford base32 (`0123456789ABCDEFGHJKMNPQRSTVWXYZ`) — excludes I/L/O/U
 * for visual unambiguity. We also apply lookalike correction on input (I→1, L→1,
 * O→0, U→V) so pasted or hand-typed codes forgive common mistakes.
 *
 * Identifier = 4 base32 chars = 20 bits of randomness (~1M unique codes).
 * MAC = 8 base32 chars = 40 bits of HMAC-SHA256 truncation (~1T guesses to forge).
 *
 * Shared secret lives in `./licenseSecret.json` so this file and
 * `scripts/generate-license.js` agree on the same HMAC key.
 *
 * SECURITY POSTURE: the secret ships inside the Electron bundle — acceptable for
 * tester-phase trust-based distribution, but when we move to paid subscription the
 * validation MUST move server-side. Anyone with the bundle can extract the secret
 * and mint their own codes.
 */
import licenseSecret from './licenseSecret.json';

const { prefix, secret, alphabet } = licenseSecret;

const IDENTIFIER_CHARS = 4;
const MAC_BYTES = 5; // 40 bits → exactly 8 base32 chars
const MAC_CHARS = 8;
const BODY_CHARS = IDENTIFIER_CHARS + MAC_CHARS; // 12

// O(1) char → 5-bit index lookup for decode validation.
const CHAR_INDEX = Object.create(null);
for (let i = 0; i < alphabet.length; i++) {
  CHAR_INDEX[alphabet[i]] = i;
}

// Lookalike fixups — applied after uppercasing + hyphen/whitespace strip. Users
// who type 'I' when they meant '1' (or O→0, L→1, U→V) still get a valid decode
// attempt instead of a cryptic "invalid char" message.
const LOOKALIKE_MAP = { I: '1', L: '1', O: '0', U: 'V' };

function hexToBytes(hex) {
  const len = hex.length / 2;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    out[i] = parseInt(hex.substr(i * 2, 2), 16);
  }
  return out;
}

function bytesToBase32(bytes) {
  let bits = 0;
  let value = 0;
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i];
    bits += 8;
    while (bits >= 5) {
      out += alphabet[(value >>> (bits - 5)) & 0x1f];
      bits -= 5;
    }
  }
  if (bits > 0) {
    out += alphabet[(value << (5 - bits)) & 0x1f];
  }
  return out;
}

/**
 * Normalize raw user input into the canonical compressed form:
 *   - strip whitespace + hyphens
 *   - uppercase
 *   - apply lookalike fixups (I→1, L→1, O→0, U→V) ONLY to the body, not the prefix
 *
 * The carve-out matters because the prefix (JRNFI) contains `I` — if we
 * substituted globally, a correctly-typed code would be rewritten to `JRNF1…`
 * and then fail the prefix check. Preserving the prefix verbatim fixes that
 * and still lets the body benefit from typo tolerance.
 *
 * Returns '' for non-string or empty input.
 */
export function normalizeCode(raw) {
  if (typeof raw !== 'string') return '';
  const stripped = raw.trim().toUpperCase().replace(/[\s-]+/g, '');

  if (stripped.startsWith(prefix)) {
    const body = stripped.slice(prefix.length);
    let fixedBody = '';
    for (let i = 0; i < body.length; i++) {
      fixedBody += LOOKALIKE_MAP[body[i]] || body[i];
    }
    return prefix + fixedBody;
  }

  // Fallback: prefix didn't match on the raw uppercase — apply lookalikes
  // globally so the downstream validator can surface a specific error
  // (invalid char, wrong length, etc.) rather than the generic prefix miss.
  let out = '';
  for (let i = 0; i < stripped.length; i++) {
    const c = stripped[i];
    out += LOOKALIKE_MAP[c] || c;
  }
  return out;
}

/**
 * Format a (possibly raw) license code into the display form `JRNFI-XXXX-XXXX-XXXX`.
 * If the input isn't well-formed, returns the normalized form untouched so the
 * UI still has a predictable string to show back.
 */
export function formatLicenseCode(raw) {
  const n = normalizeCode(raw);
  if (!n.startsWith(prefix)) return n;
  const body = n.slice(prefix.length);
  if (body.length !== BODY_CHARS) return n;
  return `${prefix}-${body.slice(0, 4)}-${body.slice(4, 8)}-${body.slice(8, 12)}`;
}

async function computeMac(identifier) {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    throw new Error('Web Crypto API unavailable — license validation needs a modern renderer.');
  }
  const msg = `${prefix}:${identifier}`;
  const key = await crypto.subtle.importKey(
    'raw',
    hexToBytes(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', key, new TextEncoder().encode(msg));
  const macBytes = new Uint8Array(sig).slice(0, MAC_BYTES);
  return bytesToBase32(macBytes).slice(0, MAC_CHARS);
}

// Constant-time string compare — avoids timing side channel on MAC check. Not
// strictly required for a client-side validator (the attacker already has the
// secret if they want to mint codes) but it's the right default to carry
// forward when this eventually moves server-side.
function constantTimeEqual(a, b) {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

/**
 * Validate a license code string. Returns:
 *   { valid: true, identifier, formatted, tier: 'pro' }
 * or
 *   { valid: false, reason: string }
 *
 * All minted codes unlock Pro for life on a single install (per Phase 11 plan).
 * We do NOT enforce single-install uniqueness here — that requires a server. For
 * tester phase this is fine: codes are given out personally and tracked externally.
 */
export async function validateLicenseCode(raw) {
  const normalized = normalizeCode(raw);

  if (!normalized) {
    return { valid: false, reason: 'Enter a code to continue.' };
  }
  if (!normalized.startsWith(prefix)) {
    return { valid: false, reason: `Code should start with ${prefix}-.` };
  }
  const body = normalized.slice(prefix.length);
  if (body.length !== BODY_CHARS) {
    return {
      valid: false,
      reason: `Code body should be ${BODY_CHARS} characters (like ${prefix}-XXXX-XXXX-XXXX).`,
    };
  }
  for (let i = 0; i < body.length; i++) {
    if (CHAR_INDEX[body[i]] === undefined) {
      return { valid: false, reason: `Invalid character '${body[i]}' in code.` };
    }
  }

  const identifier = body.slice(0, IDENTIFIER_CHARS);
  const providedMac = body.slice(IDENTIFIER_CHARS);

  let expectedMac;
  try {
    expectedMac = await computeMac(identifier);
  } catch (err) {
    return { valid: false, reason: err.message || 'Failed to check code.' };
  }

  if (!constantTimeEqual(providedMac, expectedMac)) {
    return { valid: false, reason: "This code isn't valid. Double-check and try again." };
  }

  return {
    valid: true,
    identifier,
    formatted: `${prefix}-${body.slice(0, 4)}-${body.slice(4, 8)}-${body.slice(8, 12)}`,
    tier: 'pro',
  };
}

export const LICENSE_PREFIX = prefix;
export const LICENSE_BODY_CHARS = BODY_CHARS;
